import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import { useHistory } from "react-router-dom";

import { IOffers } from "features/offers/offers.interface";
import { OFFER_MAPPING } from "features/offers/offers.config";
import {
  formattedTitle,
  handleShareOffer,
  routeToOfferDetails,
} from "features/offers/offers-utils";
import Timer from "../timer/Timer";
import defaultImg from "assets/images/offers/offer2.png";
import OfferDetailModal from "../OfferDetailModal/OfferDetailModal";
import { useAppDispatch } from "store/store";

import "./OfferCard.scss";

export interface IOfferCardProps {
  data: IOffers;
  variant?: "monthly-deal" | "odd-carousel" | "even-carousel" | "offer-listing";
  showViewButton?: boolean;
  onClick?: () => void;
  className?: string;
  isOfferListing?: boolean;
}

const OfferCard: React.FC<IOfferCardProps> = ({
  data,
  variant = "monthly-deal",
  showViewButton = false,
  onClick,
  isOfferListing = false,
  className = "",
}) => {
  const { t } = useTranslation();
  const history = useHistory();
  const dispatch = useAppDispatch();
  const [showModal, setShowModal] = useState(false);

  const buildStayText = (offerData: IOffers) => {
    let text = "";
    if (offerData?.arrival_date_gt && offerData?.departure_date_lt) {
      text = `${t("offers.fromTxt")} ${format(
        new Date(offerData?.arrival_date_gt),
        "LLL dd",
      )} ${t("offers.toText")} ${format(
        new Date(offerData.departure_date_lt),
        "LLL dd",
      )}`;
      text = offerData?.length_of_stay
        ? `${t("offers.losTxt1")} ${offerData.length_of_stay} ${t(
            "offers.losTxt3",
          )} ${text}`
        : `${t("offers.losTxt1")} ${text}`;
      return text;
    }
    if (offerData?.length_of_stay) {
      text = `${t("offers.losTxt1")} ${offerData.length_of_stay} ${t(
        "offers.losTxt2",
      )}`;
    }
    return text;
  };

  const handleViewMore = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowModal(true);
  };

  const renderMonthlyDealCard = () => (
    <div
      className="offers-wrapper"
      onClick={() => {
        if (!isOfferListing) routeToOfferDetails(data, history, dispatch);
      }}
    >
      <div className="offers-inner-wrapper">
        <div
          className="deal-img"
          style={{ backgroundImage: `url(${data.image || defaultImg})` }}
        >
          <p className="category">
            {isOfferListing
              ? t("offers.offerApplied")
              : t(OFFER_MAPPING[data.type])}
          </p>
        </div>
        <div className="deal-content">
          {formattedTitle(
            data.title || t("offers.hurry"),
            data.highlight_text,
            "deal-title",
            "deal-offer",
            false,
          )}
          {formattedTitle(
            data.description_1,
            data.highlight_text,
            "deal-subtitle",
            "deal-offer",
            true,
          )}
          {(data.arrival_date_gt && data.departure_date_lt) ||
          data.length_of_stay ? (
            <p className="desc-green">{buildStayText(data)}</p>
          ) : null}
          {data.expiry ? (
            <Timer expiryTimestamp={new Date(data.expiry).getTime()} />
          ) : null}
          <div className="card-actions">
            <button
              className="main-button"
              onClick={handleViewMore}
              type="button"
            >
              {t("offers.viewMore")}
            </button>
            <button
              className="outline-button"
              onClick={(e) => handleShareOffer(data, t, dispatch, e)}
              type="button"
              title={t("offers.shareOffer")}
            >
              {/* <i className="fas fa-share-alt"></i> */}
              {t("offers.shareOffer")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  const renderOfferListingCard = () => (
    <div
      className="offers-wrapper"
      onClick={() => {
        if (!isOfferListing) routeToOfferDetails(data, history, dispatch);
      }}
    >
      <div className="offers-inner-wrapper offer-listing-card">
        <div
          className="deal-img"
          style={{ backgroundImage: `url(${data.image || defaultImg})` }}
        >
          <p className="category">
            {isOfferListing
              ? t("offers.offerApplied")
              : t(OFFER_MAPPING[data.type])}
          </p>
          <div className="card-actions">
            <button
              className="main-button"
              onClick={handleViewMore}
              type="button"
            >
              {t("offers.viewMore")}
            </button>
            <button
              className="outline-button"
              onClick={(e) => handleShareOffer(data, t, dispatch, e)}
              type="button"
              title={t("offers.shareOffer")}
            >
              {/* <i className="fas fa-share-alt"></i> */}
              {t("offers.shareOffer")}
            </button>
          </div>
        </div>
        <div className="deal-content">
          {formattedTitle(
            data.title || t("offers.hurry"),
            data.highlight_text,
            "deal-title",
            "deal-offer",
            false,
          )}
          {formattedTitle(
            data.description_1,
            data.highlight_text,
            "deal-subtitle",
            "deal-offer",
            true,
          )}
          <div className="flex" style={{ gap: "5px" }}>
            <div className="flex-column">
              {(data.arrival_date_gt && data.departure_date_lt) ||
              data.length_of_stay ? (
                <p className="desc-green">{buildStayText(data)}</p>
              ) : null}
              {data.expiry ? (
                <Timer expiryTimestamp={new Date(data.expiry).getTime()} />
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderOddCarouselCard = () => (
    <div className="offers-wrapper">
      <div
        className="offers-inner-wrapper"
        onClick={() => routeToOfferDetails(data, history, dispatch)}
      >
        <div className="deal-img">
          <img src={data.image || defaultImg} alt="" />
        </div>
        <div className="deal-content carousel-card-content">
          {formattedTitle(
            data.title,
            data.highlight_text,
            "category text-dark",
            "deal-offer",
            false,
          )}
          {formattedTitle(
            data.description_1,
            data.highlight_text,
            "deal-text text-dark",
            "deal-offer",
            true,
          )}
          {(data.arrival_date_gt && data.departure_date_lt) ||
          data.length_of_stay ? (
            <p className="desc-green">{buildStayText(data)}</p>
          ) : null}

          <div className="card-actions">
            <button
              className="main-button"
              onClick={handleViewMore}
              type="button"
            >
              {t("offers.viewMore")}
            </button>
            <button
              className="outline-button"
              onClick={(e) => handleShareOffer(data, t, dispatch, e)}
              type="button"
              title={t("offers.shareOffer")}
            >
              {/* <i className="fas fa-share-alt"></i> */}
              {t("offers.shareOffer")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  const renderEvenCarouselCard = () => (
    <>
      <div
        className="even-carousel-card"
        onClick={() => routeToOfferDetails(data, history, dispatch)}
      >
        <div className="deal-img">
          <img src={data.image || defaultImg} alt="" />
        </div>
        <div className="deal-content carousel-card-content">
          {formattedTitle(
            data.title,
            data.highlight_text,
            "category",
            "deal-offer",
            false,
          )}
          {formattedTitle(
            data.description_1,
            data.highlight_text,
            "deal-text text-dark",
            "deal-offer",
            true,
          )}
          {data.description_2 ? (
            <div style={{ marginTop: "5px" }}>
              {formattedTitle(
                data.description_2,
                data.highlight_text,
                "desc",
                "deal-offer",
                true,
              )}
            </div>
          ) : null}
          {(data.arrival_date_gt && data.departure_date_lt) ||
          data.length_of_stay ? (
            <p className="desc-green">{buildStayText(data)}</p>
          ) : null}
          {/* {showViewButton && onClick && (
          <button
          className="desc-button primary-button"
          type="button"
          onClick={onClick}
          >
          {t("offers.view")}
          </button>
          )} */}
          <div className="card-actions">
            <button
              className="main-button"
              onClick={handleViewMore}
              type="button"
            >
              {t("offers.viewMore")}
            </button>
            <button
              className="outline-button"
              onClick={(e) => handleShareOffer(data, t, dispatch, e)}
              type="button"
              title={t("offers.shareOffer")}
            >
              {/* <i className="fas fa-share-alt"></i> */}
              {t("offers.shareOffer")}
            </button>
          </div>
        </div>
      </div>
    </>
  );

  const renderCard = () => {
    switch (variant) {
      case "monthly-deal":
        return renderMonthlyDealCard();
      case "odd-carousel":
        return renderOddCarouselCard();
      case "even-carousel":
        return renderEvenCarouselCard();
      case "offer-listing":
        return renderOfferListingCard();
      default:
        return renderMonthlyDealCard();
    }
  };

  return (
    <>
      {renderCard()}
      {showModal && (
        <OfferDetailModal
          offer={data}
          isOpen={showModal}
          onClose={() => setShowModal(false)}
          isOfferListing={isOfferListing}
        />
      )}
    </>
  );
};

export default OfferCard;
